import mentoringRouteManifest from './mentoringRouteManifest.json';

export const MENTORING_LOCALES = ['fr', 'en'];
export const MENTORING_PAGES = Object.freeze(Object.keys(mentoringRouteManifest.pages));

const pageRoutes = new Map(
  MENTORING_PAGES.flatMap((pageKey) => MENTORING_LOCALES.map((locale) => {
    const path = mentoringRouteManifest.pages[pageKey][locale];
    return [path, { locale, pageKey }];
  })),
);

const recoveryRoutes = new Map(
  MENTORING_PAGES.flatMap((pageKey) => MENTORING_LOCALES.map((locale) => {
    const path = mentoringRouteManifest.pages[pageKey][locale];
    return [`${path}/*`, { locale, pageKey }];
  })),
);

const allPaths = MENTORING_PAGES.flatMap((pageKey) => (
  MENTORING_LOCALES.map((locale) => mentoringRouteManifest.pages[pageKey][locale])
));

const normalizePathname = (pathname) => (
  typeof pathname === 'string'
    ? (pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname)
    : ''
);

const getPathLocale = (pathname) => (pathname.startsWith('/en') ? 'en' : 'fr');

/**
 * Absolute path for a mentoring page, or undefined when the key/locale is
 * unknown. Callers use this to build internal links so a page always matches
 * the document language the visitor is currently reading.
 */
export const getMentoringPath = (locale, pageKey) => (
  MENTORING_LOCALES.includes(locale)
    ? mentoringRouteManifest.pages[pageKey]?.[locale]
    : undefined
);

/**
 * Resolves a pathname to a mentoring route. Prefix matches let unknown
 * sub-paths (`/en/mentoring/program/anything`) fall through to the closest
 * real page instead of hard-404ing.
 */
export const resolveMentoringRoute = (pathname) => {
  const normalizedPathname = normalizePathname(pathname);
  const exact = pageRoutes.get(normalizedPathname) || recoveryRoutes.get(normalizedPathname);

  if (exact) {
    return { type: 'page', ...exact };
  }

  const prefixMatch = allPaths
    .filter((path) => normalizedPathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  if (prefixMatch) {
    return { type: 'page', ...pageRoutes.get(prefixMatch) };
  }

  return { type: 'not-found', locale: getPathLocale(normalizedPathname || '') };
};

/**
 * hreflang alternates for a page. The fr URL is always x-default because
 * French is the site-wide fallback language.
 */
export const getMentoringAlternates = (pageKey) => {
  const paths = mentoringRouteManifest.pages[pageKey];

  if (!paths) {
    return undefined;
  }

  return { fr: paths.fr, en: paths.en, xDefault: paths.fr };
};
